// src/cluster.js
//
// A hub with hundreds of direct children reads as a hairball, not a graph —
// this is the fix: introduce synthetic "cluster" nodes between the hub and
// its leaves, one level per grouping criterion, in whatever order the
// caller wants. Ask for [by_kind] and you get hub -> kind clusters ->
// leaves; ask for [by_geography, by_kind] and you get hub -> province
// clusters -> kind clusters (within that province) -> leaves — same
// function, order decides the shape.
//
// Deliberately plain synthetic nodes with plain edges, never a
// driver-specific feature like Cytoscape's compound/parent nodes: a
// cluster made this way renders correctly on any of the three engines this
// library drives, today or once vis-network/force-graph exist.
//
// Not specific to the RAG entity graph, or to places, or to Tiziano — any
// future hub (a product catalogue, a client map) with the same "too many
// direct children" problem uses the same function.

/**
 * @typedef {object} ClusterLevel
 * @property {string} key                       a short id for this criterion, e.g. 'kind' — becomes part of every cluster id at this level, so two levels must not share one
 * @property {(leaf: object) => string} extract  the grouping value for one leaf, e.g. n => n.group
 * @property {(value: string, count_n: number) => string} [label]  defaults to "value (count)"
 */

/**
 * @param {string} hub_id                the existing node every top-level cluster (or ungrouped leaf) attaches to
 * @param {Array<{id: string, [key: string]: any}>} leaves
 * @param {ClusterLevel[]} levels        empty array = no clustering, every leaf attaches straight to hub_id (same as not calling this at all)
 * @param {{threshold_n?: number}} [options]  a group smaller than this (default 4) is not worth a cluster node —
 *        its members attach straight to whatever the current parent is, and no deeper level runs for them either:
 *        a lone item in a two-person cluster does not need its kind broken out further
 * @returns {{nodes: Array<{id: string, label: string, group: 'cluster', level_c: string}>, edges: Array<{source: string, target: string}>}}
 *          new cluster nodes and all the edges connecting hub -> ... -> leaves; leaf nodes themselves are not
 *          returned (the caller already has them) — concatenate these edges with the leaves' own node array
 */
export function clusterHierarchy(hub_id, leaves, levels, options = {}) {
    const threshold_n = options.threshold_n ?? 4;
    const nodes = [];
    const edges = [];

    (function recurse(items, path, parent_id, level_i) {
        if (level_i >= levels.length) {
            for (const item of items) {
                edges.push({ source: item.id, target: parent_id });
            }

            return;
        }

        const level = levels[level_i];
        const groups = new Map();

        for (const item of items) {
            const value = level.extract(item);
            const bucket = groups.get(value) ?? [];
            bucket.push(item);
            groups.set(value, bucket);
        }

        for (const [value, members] of groups) {
            // Too small to earn a cluster at this level — attach directly and
            // stop: a lone member of a two-person "kind" group does not also
            // get its own geography sub-cluster one level deeper.
            if (members.length < threshold_n) {
                for (const item of members) {
                    edges.push({ source: item.id, target: parent_id });
                }

                continue;
            }

            const cluster_path = [...path, `${level.key}:${value}`];
            const cluster_id = cluster_path.join('|');
            const label = level.label ? level.label(value, members.length) : `${value} (${members.length})`;

            nodes.push({ id: cluster_id, label, group: 'cluster', level_c: level.key });
            edges.push({ source: cluster_id, target: parent_id });

            recurse(members, cluster_path, cluster_id, level_i + 1);
        }
    })(leaves, [], hub_id, 0);

    return { nodes, edges };
}
